import {
    MessageAuthorType,
    Ticket,
    TicketEvent,
    TicketEventType,
    TicketMessage,
} from '../models/chamados.models';

/**
 * Timeline do chamado: mensagens e eventos numa linha do tempo só.
 *
 * As duas coisas moram em lugares diferentes no banco — mensagem é coleção
 * separada (cresce sem teto, é paginada) e evento é array embutido no chamado
 * (append-only, dezenas de itens). Para a tela, porém, elas são a mesma
 * história: "o cliente escreveu, o atendente assumiu, o SLA estourou, o
 * atendente respondeu". Mesclar aqui, numa função pura e testável, evita o
 * template ter que reconciliar duas listas na hora de desenhar.
 */

export type TimelineEntryKind = 'MENSAGEM' | 'EVENTO';

export interface TimelineEntry {
    kind: TimelineEntryKind;
    /** Momento usado na ordenação. */
    em: Date;
    /** Preenchido quando kind === 'MENSAGEM'. */
    mensagem?: TicketMessage;
    /** Preenchido quando kind === 'EVENTO'. */
    evento?: TicketEvent;
}

/**
 * Converte a data que vem do backend.
 *
 * O findnative devolve data de duas formas conforme o campo e o driver: string
 * ISO ou objeto com `$date`. Tratar as duas aqui evita `Invalid Date` espalhado
 * pela tela — e uma entrada com data inválida iria parar no topo da lista, no
 * lugar mais visível possível.
 */
export function parseBackendDate(valor: unknown): Date | null {

    if (valor == null) {
        return null;
    }

    if (valor instanceof Date) {
        return valor;
    }

    if (typeof valor === 'string') {
        const data = new Date(valor);
        return isNaN(data.getTime()) ? null : data;
    }

    if (typeof valor === 'object') {
        const comDate = valor as { $date?: string | number };
        if (comDate.$date != null) {
            const data = new Date(comDate.$date);
            return isNaN(data.getTime()) ? null : data;
        }
    }

    return null;
}

/**
 * Mescla mensagens e eventos, em ordem cronológica crescente.
 *
 * `mostrarInternas` existe porque nota interna não pode vazar para uma tela
 * que o solicitante enxergue — a mesa mostra, o acompanhamento do cliente não.
 *
 * Entrada sem data reconhecível é DESCARTADA em vez de ir para o começo ou o
 * fim: numa linha do tempo, item sem tempo não tem lugar honesto.
 */
export function buildTimeline(
    ticket: Ticket,
    mensagens: TicketMessage[],
    mostrarInternas: boolean
): TimelineEntry[] {

    const entradas: TimelineEntry[] = [];

    (mensagens ?? []).forEach(mensagem => {

        if (mensagem.interna === true && !mostrarInternas) {
            return;
        }

        const em = parseBackendDate(mensagem.em);
        if (em == null) {
            return;
        }

        entradas.push({ kind: 'MENSAGEM', em: em, mensagem: mensagem });
    });

    (ticket?.eventos ?? []).forEach(evento => {

        const em = parseBackendDate(evento.em);
        if (em == null) {
            return;
        }

        entradas.push({ kind: 'EVENTO', em: em, evento: evento });
    });

    entradas.sort((a, b) => a.em.getTime() - b.em.getTime());

    return entradas;
}

/** Ícone do NG-ZORRO por tipo de evento. */
export function eventIcon(tipo: TicketEventType): string {
    switch (tipo) {
        case 'ABERTURA': return 'plus-circle';
        case 'STATUS': return 'swap';
        case 'ATRIBUICAO': return 'user';
        case 'TRANSFERENCIA': return 'swap-right';
        case 'ESCALONAMENTO': return 'rise';
        case 'SLA': return 'clock-circle';
        case 'HANDOFF': return 'robot';
        case 'REABERTURA': return 'reload';
        case 'ENCERRAMENTO': return 'check-circle';
        case 'CSAT': return 'star';
        default: return 'info-circle';
    }
}

/**
 * Cor do autor na conversa.
 *
 * Nota interna tem cor própria mesmo sendo escrita por um atendente: é o aviso
 * visual de que aquilo NÃO foi visto pelo cliente, que é a informação mais
 * importante da mensagem.
 */
export function authorColor(autorTipo: MessageAuthorType, interna: boolean): string {

    if (interna) {
        return 'gold';
    }

    switch (autorTipo) {
        case 'CLIENTE': return 'default';
        case 'ATENDENTE': return 'blue';
        case 'BOT': return 'purple';
        case 'SISTEMA': return 'default';
        default: return 'default';
    }
}
